import { Key, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { RiArrowLeftSLine } from 'react-icons/ri';

import { AuthContext } from 'context/AuthContext';

import ArticlePlaceholder from 'components/ui/article-placeholder';

import Box from 'components/ui/box';
import {
  getPlayerInfo,
  getRealCycles,
  getServerTime,
  getTaskCycles,
  getTasks,
  getTasksManager,
} from 'services/dashboard.service';
import { TaskItem } from 'components/task-item';
import { GetTaskStatusLabel } from 'utils/strings';

import ImgFeedback from 'assets/img/comportamento/bg-missoes-not-found.png';
import Button from 'components/ui/button';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { BsPlusLg } from 'react-icons/bs';
import { BiMinus } from 'react-icons/bi';
import CustomSelect from 'components/custom-select';

interface Props {
  id: string;
  isFromTeam?: boolean;
}

export default function MissaoDestaquePorUsuario({ id, isFromTeam }: Props) {
  const { user, realCycle } = useContext(AuthContext);
  const navigate = useNavigate();

  const [realDatetime, setRealDatetime] = useState<number>(0);

  const [playerInfo, setPlayerInfo] = useState<any>();
  const [isGestor, setIsGestor] = useState<boolean | undefined>();
  const [loading, setLoading] = useState(true);
  const [myTasks, setMyTasks] = useState<any[]>();
  const [otherTasks, setOtherTasks] = useState<any[]>();

  const [taskCycles, setTaskCycles] = useState<any>();
  const [taskCyclesAll, setTaskCyclesAll] = useState<any>();

  const [tab, setTab] = useState<'cadastrada' | 'participando'>('cadastrada');
  const [cycleActive, setCycleActive] = useState<string>();
  const [showMore, setShowMore] = useState(false);
  const [numberOfElements, setNumberOfElements] = useState(3);
  const [optionsCycle, setOptionsCycle] = useState<any[] | undefined>([]);
  const [activeItem, setActiveItem] = useState<any>();
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    let interv: any = undefined;

    getServerTime().then(timestamp => {
      setRealDatetime(timestamp);

      interv = window.setInterval(() => {
        setRealDatetime(prevstate => prevstate + 1000);
      }, 1000);
    });

    return () => clearInterval(interv);
  }, []);

  const activeCycleObj = useMemo(() => taskCycles?.find((item: any) => item._id === cycleActive), [cycleActive, taskCycles]);

  const tasksPerCycle: any = useMemo(() => {
    if (!Array.isArray(myTasks) || !Array.isArray(otherTasks) || !taskCycles?.length || !cycleActive) return null;
    const cycle = taskCycles.find((cycle: any) => cycle._id === cycleActive);
    const tasks = [...(tab === 'cadastrada' ? myTasks : otherTasks)]?.filter(
      item => item.created >= cycle?.date_start && item?.created <= cycle?.date_finish,
    );

    return tasks?.length ? tasks : [];
  }, [cycleActive, myTasks, otherTasks, tab, taskCycles]);

  const resumePerCycle: {
    registered: number;
    validated: number;
    waiting: number;
  } | null = useMemo(() => {
    if (!Array.isArray(myTasks) || !Array.isArray(otherTasks) || !taskCycles?.length || !cycleActive) return null;

    const cycle = taskCycles.find((cycle: any) => cycle._id === cycleActive);
    const tasks = [...(tab === 'cadastrada' ? myTasks : otherTasks)]?.filter(
      item => item.created >= cycle?.date_start && item?.created <= cycle?.date_finish,
    );

    return {
      registered: tasks.length || 0,
      validated: tasks?.filter(task => !!task.status?.match(/(aprovado|dobrado|promovido|selo|concluido)/gi))?.length || 0,
      waiting: tasks?.filter(task => !!task.status?.match(/(aguardando)/gi))?.length || 0,
    };
  }, [cycleActive, myTasks, otherTasks, tab, taskCycles]);

  const getActiveEvaluationStep: 0 | 2 | 3 | 4 | 4.1 | 5 | null = useMemo((): 0 | 2 | 3 | 4 | 4.1 | 5 | null => {
    if (!activeCycleObj || activeCycleObj.status !== 'evaluation' || !realDatetime) return null;

    if (realDatetime > activeCycleObj.date_evaluation_step2_start && realDatetime < activeCycleObj.date_evaluation_step2_finish) {
      // Etapa 2
      return 2;
    } else if (
      realDatetime > activeCycleObj.date_evaluation_step3_start &&
      realDatetime < activeCycleObj.date_evaluation_step3_finish
    ) {
      // Etapa 3
      return 3;
    } else if (
      realDatetime > activeCycleObj.date_evaluation_step4_start &&
      realDatetime < activeCycleObj.date_evaluation_step4_finish
    ) {
      // Etapa 4
      return 4;
    } else if (
      realDatetime > activeCycleObj.date_evaluation_pitStop_start &&
      realDatetime < activeCycleObj.date_evaluation_pitStop_finish
    ) {
      // Pit Stop
      return 4.1;
    } else if (
      realDatetime > activeCycleObj.date_evaluation_step5_start &&
      realDatetime < activeCycleObj.date_evaluation_step5_finish
    ) {
      // Etapa 5
      return 5;
    } else {
      return 0;
    }
  }, [activeCycleObj, realDatetime]);

  const taskEditable = useCallback(
    (task: any): boolean => {
      if (!activeCycleObj || !user) return false;

      if (tab === 'cadastrada' && task.owner === user._id) {
        if (
          task.status === 'aguardando' &&
          activeCycleObj.status === 'open' &&
          task.created >= activeCycleObj.date_start &&
          task.created <= activeCycleObj.date_finish
        ) {
          return true;
        }

        if (
          task.status === 'rejeitado' &&
          getActiveEvaluationStep &&
          getActiveEvaluationStep === 2 &&
          task.created >= activeCycleObj.date_start &&
          task.created <= activeCycleObj.date_evaluation
        ) {
          return true;
        }

        return false;
      }

      return false;
    },
    [activeCycleObj, getActiveEvaluationStep, tab, user],
  );

  function renderTasksPerCycle() {
    // if (!resumePerCycle) return null;

    try {
      if (tasksPerCycle?.length) {
        return tasksPerCycle.map((task: { _id: Key | null | undefined; owner: string | undefined; status: string }) => (
          <div key={task._id}>
            <TaskItem
              data={task}
              editable={taskEditable(task)}
              onDelete={() => loadData()}
              onReload={() => loadData()}
              cycle={activeCycleObj}
              playerInfo={playerInfo}
              isView={true}
            />
          </div>
        ));
      } else {
        return (
          <div className="flex justify-center items-center flex-col space-y-2 lg:space-y-8 bg-maastricht-blue-3 rounded-[20px] p-8 text-center">
            <img src={ImgFeedback} className="max-w-[150px]" alt=" " />
            <p className="text-lg block text-center font-thin text-white leading-loose tracking-tight">
              <>Nenhuma realização cadastrada</>
            </p>
          </div>
        );
      }
    } catch (e) {
      return null;
    }
  }

  useEffect(() => {
    if (taskCycles?.length) {
      const currentCycle = taskCycles?.find((item: any) => item.status === 'open');
      setCycleActive(currentCycle?._id || taskCycles[0]._id);
    }
  }, [tab, taskCycles]);

  async function loadData() {
    if (!realCycle?.[0]) return;

    const playerInfoResult = await getPlayerInfo(id);

    setPlayerInfo(playerInfoResult);

    setIsGestor(playerInfoResult?.cad_missao && playerInfoResult?.cad_missao === 0);

    const isGestor = playerInfoResult.tipo_ro === 'GERENCIAL' || playerInfoResult.funcao === 'DIRETOR';

    if (isGestor) {
      const dataGestor = await getTasksManager(id, activeCycleObj.date_start, activeCycleObj.date_finish);
      const dataEquipe = await getTasks(id, activeCycleObj.date_start, activeCycleObj.date_finish);
      setMyTasks([...dataGestor, ...dataEquipe]);
      setOtherTasks([...dataGestor, ...dataEquipe]);
    } else {
      const data = await getTasks(id, activeCycleObj.date_start, activeCycleObj.date_finish);
      setMyTasks(data);
      setOtherTasks(data);
    }
    // const data = await getTasks(id);

    // setMyTasks(data);
    // setOtherTasks(data);

     // if (!cycleActive) {
    //   const taskCycleResults = await getRealCycles();
    //   setTaskCycles(taskCycleResults.filter((item: any) => item.year > 2022)[0].steps);
    //   setTaskCyclesAll(taskCycleResults.filter((item: any) => item.year > 2022));

    //   const currentCycle = taskCycleResults.filter((item: any) => item.year > 2022).find((item: any) => item.status === 'open');

    //   setCycleActive(currentCycle?._id || taskCycleResults[0].steps[0]._id);
    // }

    setLoading(false);
  }

  useEffect(() => {
    if (!realCycle) return;

    const cycleOptions = realCycle.map((cycle: any) => {
      cycle.label = 'Ciclo ' + cycle.title;
      cycle.value = cycle._id;

      return cycle;
    });

    setOptionsCycle(cycleOptions);
    if (!activeItem) {
      setActiveItem(cycleOptions[0].value);
      setTaskCycles(cycleOptions[0].steps);
      setTaskCyclesAll(realCycle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realCycle]);

  useEffect(() => {
    if (!id || !realCycle || !activeCycleObj) return;

    setLoading(true);

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, realCycle, activeCycleObj]);

  useEffect(() => {
    if (!taskCycles) return;

    if (taskCycles.length <= numberOfElements) {
      setShowMore(false);
    } else {
      setShowMore(true);
    }
  }, [numberOfElements, taskCycles]);

  useEffect(() => {
    if (!activeItem) return;

    const filtredData = taskCyclesAll?.filter((item: any) => item._id === activeItem);
    if (!!filtredData?.length) {
      setTaskCycles(filtredData[0].steps);
    }
  }, [activeItem, taskCyclesAll]);

  function getIndexByValue(array: any, value: string) {
    return array.findIndex((element: any) => element.value === value);
  }

  return (
    <>
      <div className="mx-auto container p-4">
        <div className="w-full flex flex-col lg:flex-row lg:justify-between">
          <div className="lg:w-[32.5%]">
            <div className="mb-5">
              <h2 className="text-xl text-white block mb-6 font-semibold">Histórico de Realizações Destaque</h2>

              <Box className="relative bg-prussian-blue border-electric-blue p-4 lg:p-8 border-opacity-100 bg-opacity-100 w-full rounded-[20px]">
                {loading ? (
                  <div className="grid grid-cols-1 gap-3">
                    <ArticlePlaceholder />
                  </div>
                ) : (
                  <>
                    <div className="px-8 mb-8">
                      <div>
                        <p className="mb-2">Escolha o ciclo que deseja acompanhar</p>

                        {optionsCycle && (
                          <div className="mb-4">
                            <CustomSelect
                              placeholder="Clique para selecionar o ciclo"
                              options={optionsCycle}
                              onChange={(e: any) => setActiveItem(e.value)}
                              value={optionsCycle[getIndexByValue(optionsCycle, activeItem)]}
                            />
                          </div>
                        )}
                      </div>

                      <p className="text-white mb-8 text-base font-normal block">Escolha a fase</p>

                      <ul className="space-y-4 flex flex-col">
                        {taskCycles &&
                          taskCycles.slice(0, numberOfElements).map((cycle: any) => (
                            <li key={cycle._id}>
                              <button
                                className={`${
                                  cycleActive === cycle._id ? 'text-alice-blue bg-slate-blue' : 'text-white'
                                } text-sm flex items-center justify-center font-semibold uppercase ring-2 ring-slate-blue rounded w-full h-10`}
                                onClick={() => setCycleActive(cycle._id)}
                              >
                                Fase {cycle.title}
                                {/* {GetTaskStatusLabel(cycle.status)} */}
                              </button>
                            </li>
                          ))}

                        {taskCycles.length > 3 && (
                          <button
                            className="mt-4 mx-auto flex items-center text-grey-light justify-center lg:justify-start"
                            onClick={() => (!showMore ? setNumberOfElements(3) : setNumberOfElements(numberOfElements + 3))}
                          >
                            {!showMore ? (
                              <>
                                <BiMinus className="mr-2 inline-block grey-dark" size={17} />
                                <span className="uppercase">Ver menos</span>
                              </>
                            ) : (
                              <>
                                <BsPlusLg className="mr-2 inline-block grey-dark" size={17} />
                                <span className="uppercase">Ver mais</span>
                              </>
                            )}
                          </button>
                        )}
                      </ul>
                    </div>

                    <div className="bg-maastricht-blue rounded-3xl p-8">
                      <p className="block text-white leading-none font-normal text-base mb-4">
                        Realizações Destaque de {activeCycleObj?.title}
                      </p>

                      <ul className="flex flex-col w-full">
                        <li className="flex items-center justify-between text-white font-light text-xs leading-none mb-4 pb-4 border-b border-prussian-blue-2">
                          {tab === 'cadastrada'
                            ? 'Total de realizações cadastradas'
                            : 'Total de realizações cadastradas que você participa'}{' '}
                          <span className="text-base text-yellow leading-none font-semibold flex-1 text-right pl-2">
                            {resumePerCycle?.registered || '0'}
                          </span>
                        </li>
                        <li className="flex items-center justify-between text-white font-light text-xs leading-none mb-4 pb-4 border-b border-prussian-blue-2">
                          {tab === 'cadastrada'
                            ? 'Total de realizações validadas'
                            : 'Total de realizações validadas que você participa'}{' '}
                          <span className="text-base text-yellow leading-none font-semibold flex-1 text-right pl-2">
                            {resumePerCycle?.validated || '0'}
                          </span>
                        </li>
                        <li className="flex items-center justify-between text-white font-light text-xs leading-none">
                          {tab === 'cadastrada'
                            ? 'Total de realizações aguardando validação'
                            : 'Total de realizações aguardando validação que você participa'}{' '}
                          <span className="text-base text-yellow leading-none font-semibold flex-1 text-right pl-2">
                            {resumePerCycle?.waiting || '0'}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </Box>
            </div>
          </div>

          <div className="lg:w-[64.8%]">
            <h2 className="text-xl text-white block mb-6 font-semibold">
              Realizações Destaque da fase {activeCycleObj?.status === 'open' ? 'atual' : activeCycleObj?.title}
            </h2>

            <Box className="relative bg-prussian-blue border-electric-blue p-4 lg:p-8 border-opacity-100 bg-opacity-100 w-full rounded-[20px] space-y-6">
              {loading ? (
                <div className="grid grid-cols-1 gap-3">
                  <ArticlePlaceholder />
                </div>
              ) : (
                renderTasksPerCycle()
              )}
            </Box>
          </div>
        </div>
      </div>

      <Outlet />
    </>
  );
}
